import { parseProfileRoute, type ProfileRoute } from './profile-link-view';
import { ProfileLinksApiError } from './profile-links-error';
import type { OpenProfileThreadResult } from './profile-links-types';

/**
 * The web half of "open a profile link from the app" (docs/symphony-profile-links-design.md §6.1) — the
 * `/profile-thread?handle=&context=` route, the PENDING target that survives a sign-up, and the per-thread
 * descriptor the chat header reads. Pure apart from storage, which every function guards: a blocked or full
 * store costs resilience, never the flow.
 *
 * The phones persist the same target as `.profileLink(handle, context?)` (like `pendingPackAccept`), fired
 * once the account is connected. Web's equivalent is two-layered:
 *
 * 1. `/profile-thread` itself — a sign-in bounce returns to the URL it left, and the route is tenant-free
 *    (lib/onboarding-gate.js) so a brand-new account opens the thread BEFORE onboarding (§6.1 step 4).
 * 2. A pending record in `localStorage`, written when the visitor taps "Continue on the web" on `/me/…`, for
 *    the sign-up paths that do not come back to the URL they left (a new account lands on `/`). Any app
 *    page then resumes it once (`pendingResumePath`, driven by `PendingProfileThreadResume`).
 */

/** The in-app route that opens a profile thread. Mount-relative: prefix it with `useBasePath()`. */
export function profileThreadPath(route: ProfileRoute): string {
  const query = new URLSearchParams({ handle: route.handle });
  if (route.context) query.set('context', route.context);
  return `/profile-thread?${query.toString()}`;
}

/** The absolute target "Continue on the web" opens from the public page (which is on the root host). */
export function profileThreadWebUrl(route: ProfileRoute): string {
  return `https://symphony.wix.com${profileThreadPath(route)}`;
}

const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

/** `?handle=&context=` → the route, through the same validator as `/me/<handle>/<context>`. */
export function parseProfileThreadQuery(query: Record<string, string | string[] | undefined>): ProfileRoute | null {
  const context = first(query.context) || undefined;
  return parseProfileRoute(first(query.handle), context);
}

// ---- the pending target ----

/** A NEW key, so it is named for the product rather than inheriting the legacy `orion-web:` prefix. */
export const PENDING_KEY = 'symphony-pending-profile-thread';
/** Long enough for a sign-up with an email confirmation; short enough that a stale scan never fires. */
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

interface PendingRecord extends ProfileRoute {
  savedAt: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const storage = (): StorageLike | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export function savePendingProfileThread(route: ProfileRoute, now: number, store: StorageLike | null = storage()): void {
  try {
    store?.setItem(PENDING_KEY, JSON.stringify({ ...route, savedAt: now } satisfies PendingRecord));
  } catch {
    // The URL still carries the target; only the sign-up fallback is lost.
  }
}

export function clearPendingProfileThread(store: StorageLike | null = storage()): void {
  try {
    store?.removeItem(PENDING_KEY);
  } catch {
    // Unreachable either way.
  }
}

/** The pending target, or null when none is stored, it is malformed, or it is older than the TTL (and then it is cleared). */
export function readPendingProfileThread(now: number, store: StorageLike | null = storage()): ProfileRoute | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(PENDING_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingRecord>;
    const route = parseProfileRoute(typeof parsed.handle === 'string' ? parsed.handle : undefined, typeof parsed.context === 'string' ? parsed.context : undefined);
    const fresh = typeof parsed.savedAt === 'number' && now - parsed.savedAt >= 0 && now - parsed.savedAt <= PENDING_TTL_MS;
    if (route && fresh) return route;
  } catch {
    // Fall through: a half-written blob reads as absent.
  }
  clearPendingProfileThread(store);
  return null;
}

/**
 * Routes the resume must never interrupt: the opener itself, the public pages, the auth hops, and the
 * sign-up loader (`/welcome` creates the account; leaving it mid-way would strand the visitor without one —
 * the resume fires on the page it forwards to instead).
 */
const NO_RESUME = [/^\/profile-thread(?:\/|$)/, /^\/welcome(?:\/|$)/, /^\/onboarding(?:\/|$)/, /^\/me\//, /^\/s\//, /^\/pack-open(?:\/|$)/, /^\/pack-accept(?:\/|$)/, /^\/agent-oauth(?:\/|$)/, /^\/oauth2(?:\/|$)/];

/**
 * Where to send the reader to resume a pending profile thread, or null. Only from an in-app page.
 */
export function pendingResumePath(pending: ProfileRoute | null, pathname: string): string | null {
  if (!pending) return null;
  if (NO_RESUME.some((re) => re.test(pathname))) return null;
  return profileThreadPath(pending);
}

/**
 * The resume, as a one-shot: reads the pending target and CLEARS it before answering, so a resume fires at
 * most once per record — never again on every `/` after an open that failed. From here the target rides the
 * `/profile-thread` URL; a 401 there saves it again for the sign-in it triggers, which is the only re-arm.
 */
export function takePendingResume(pathname: string, now: number, store: StorageLike | null = storage()): string | null {
  const pending = readPendingProfileThread(now, store);
  const path = pendingResumePath(pending, pathname);
  if (path) clearPendingProfileThread(store);
  return path;
}

// ---- the thread descriptor (for the chat header) ----

export interface ProfileThreadDescriptor {
  ownerName: string;
  contextTitle?: string;
}

export const THREADS_KEY = 'symphony-profile-threads';
const MAX_THREADS = 20;

/**
 * Remember that a conversation is a profile thread, so the chat can head it "<Owner> · <context>" with the
 * disclosure (§6.6). TODO(profile-links P6 backend): the conversation itself will carry this (the outreach
 * metadata's `profileLinkId` + public card, §6.3); until then this browser remembers what the open answered,
 * newest first, capped.
 */
export function rememberProfileThread(conversationId: string, descriptor: ProfileThreadDescriptor, store: StorageLike | null = storage()): void {
  try {
    const all = readThreads(store).filter(([id]) => id !== conversationId);
    all.unshift([conversationId, { ownerName: descriptor.ownerName, contextTitle: descriptor.contextTitle || undefined }]);
    store?.setItem(THREADS_KEY, JSON.stringify(all.slice(0, MAX_THREADS)));
  } catch {
    // The thread still opens; it only loses its header in this browser.
  }
}

export function profileThreadFor(conversationId: string | null | undefined, store: StorageLike | null = storage()): ProfileThreadDescriptor | null {
  if (!conversationId) return null;
  return readThreads(store).find(([id]) => id === conversationId)?.[1] ?? null;
}

function readThreads(store: StorageLike | null): [string, ProfileThreadDescriptor][] {
  try {
    const parsed: unknown = JSON.parse(store?.getItem(THREADS_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is [string, ProfileThreadDescriptor] =>
        Array.isArray(entry) && typeof entry[0] === 'string' && !!entry[1] && typeof entry[1].ownerName === 'string' && !!entry[1].ownerName,
    );
  } catch {
    return [];
  }
}

/**
 * Where a settled open goes. A thread opens FULLSCREEN in the chat (never bare `/`, which opens the chat only
 * for a user still in onboarding — same as `packChatHref`); the owner's own link opens its editor.
 */
export function openResultHref(result: OpenProfileThreadResult): string {
  if (result.kind === 'own_link') return `/profile-links/${encodeURIComponent(result.linkId)}`;
  const params = new URLSearchParams({ conversation: result.conversationId, chat: 'fullscreen' });
  return `/?${params.toString()}`;
}

export type OpenFailure = 'signin' | 'unavailable' | 'not_ready' | 'failed';

/**
 * What a failed open means to the reader. A 401 is "sign in, then this resumes"; a 404 is the same neutral
 * "isn't available" the public page shows (never which handles exist); `not_implemented` is the backend
 * not being deployed yet; anything else is retryable.
 */
export function classifyOpenFailure(err: unknown): OpenFailure {
  if (err instanceof ProfileLinksApiError) {
    if (err.status === 401) return 'signin';
    if (err.code === 'not_implemented') return 'not_ready';
    if (err.status === 404 || err.status === 400) return 'unavailable';
  }
  return 'failed';
}
